import type React from 'react';
import type { TopicTypeId } from '@/modules/topic/types';
import { TOPIC_TYPES } from '@/modules/topic/types';
import type {
  TopicComponentSet,
  TopicReadBodyProps,
  TopicEditBodyProps,
  TopicWelcomeProps,
} from './types';

/**
 * Permanent registry mapping a topic type to its per-type body components,
 * discovered from the package layout: each `./<typeId>/` folder that exports a
 * `ReadOnlyView`, an `EditForm` and a `WelcomeInterstitial` contributes an entry
 * keyed by its folder name (which must be a `TopicTypeId`). Adding a new type is
 * therefore just dropping in its folder — no edit here.
 *
 * The bodies are the per-type scrollable content the stable `TopicReadPage` /
 * `TopicCreateOrUpdatePage` shells dispatch to once the topic (and thus its type) is known,
 * plus the welcome sheet the read shell opens for an arriving visitor; each type
 * owns — and may diverge — its own read view, edit form and welcome.
 *
 * The registry is permanent and flag-agnostic (mirroring `topicTypeRegistry`);
 * whether the component tree is used at all — rather than the legacy `TopicView` —
 * is gated by the `topicGardenEnabled` flag at the dispatch consumers
 * (`{Read,Update,Create}TopicGate`).
 */
const readOnlyViews = import.meta.glob<React.FC<TopicReadBodyProps>>(
  './*/ReadOnlyView.tsx',
  { eager: true, import: 'default' }
);
const editForms = import.meta.glob<React.FC<TopicEditBodyProps>>(
  './*/EditForm.tsx',
  { eager: true, import: 'default' }
);
const welcomeInterstitials = import.meta.glob<React.FC<TopicWelcomeProps>>(
  './*/WelcomeInterstitial.tsx',
  { eager: true, import: 'default' }
);

const VALID_TOPIC_TYPE_IDS = new Set<string>(TOPIC_TYPES.map(t => t.id));

/** The `<typeId>` folder segment of a `./<typeId>/File.tsx` glob key, if valid. */
function folderTypeId(path: string): TopicTypeId | null {
  const id = path.split('/')[1];
  return id && VALID_TOPIC_TYPE_IDS.has(id) ? (id as TopicTypeId) : null;
}

function buildRegistry(): Partial<Record<TopicTypeId, TopicComponentSet>> {
  const registry: Partial<Record<TopicTypeId, TopicComponentSet>> = {};
  for (const [path, ReadOnlyView] of Object.entries(readOnlyViews)) {
    const id = folderTypeId(path);
    if (!id) continue;
    const EditForm =
      editForms[path.replace('ReadOnlyView.tsx', 'EditForm.tsx')];
    if (!EditForm) continue;
    const WelcomeInterstitial =
      welcomeInterstitials[
        path.replace('ReadOnlyView.tsx', 'WelcomeInterstitial.tsx')
      ];
    if (!WelcomeInterstitial) continue;
    registry[id] = { ReadOnlyView, EditForm, WelcomeInterstitial };
  }
  return registry;
}

export const topicComponentRegistry: Partial<
  Record<TopicTypeId, TopicComponentSet>
> = buildRegistry();
